import { useEffect, useState, useCallback } from "react"
import axios from "axios"
import { API_URL } from "../../../../constants"
import CardMarkerSmall from "../../../../components/CockpitCards/CardMarkerSmall"
import ChangePaymentText from "./ChangePaymentText"
import ButtonsMain from "../../../../components/Button/ButtonMain"
import ButtonForm from "../../../../components/Button/ButtonForm"
import {
  Stack,
  Typography,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Slide,
  IconButton,
  useTheme,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { stripePromise } from "../../../../constants"
import { useTranslation } from "../../../../language"

const SlideUp = (props) => <Slide direction="up" {...props} />


export default function PaymentMethodCard({ sx }) {

  const theme = useTheme()
  const { t } = useTranslation()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${API_URL}/api/account/billing/`)
      setData(res.data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (loading) return <Skeleton height={180} />
  if (!data) return null

  const { current_payment_method, last_payment_status } = data

  return (
    <>
      <CardMarkerSmall
        sx={sx}
        headline={t("pay_headline")}
        subtext={<ChangePaymentText />}
        cardlines={
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CreditCardIcon />
              <Typography variant="h6">{t("pay_current_method")}</Typography>
            </Stack>

            <Stack spacing={1}>
              {current_payment_method?.type === "card" ? (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack spacing={1} sx={{ my: 2, py: 2, px: 6, bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C960", borderRadius: 3 }}>
                    <Typography fontWeight={600}>
                      {current_payment_method.brand || t("pay_card_fallback")}
                    </Typography>
                    <Typography variant="subtitle1">
                      {"**** **** **** "}{current_payment_method.last4 || "XXXX"}
                      {current_payment_method.exp_month && current_payment_method.exp_year
                        ? ` • ${t("pay_exp")} ${current_payment_method.exp_month}/${current_payment_method.exp_year}`
                        : ""}
                    </Typography>
                  </Stack>
                  <Chip
                    icon={last_payment_status === "succeeded" ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={last_payment_status === "succeeded" ? t("pay_status_active") : t("pay_status_error")}
                    color={last_payment_status === "succeeded" ? "success" : "error"}
                  />
                </Stack>
              ) : (
                <Typography>{t("pay_no_method")}</Typography>
              )}
            </Stack>

            <ButtonsMain
              action={() => setModalOpen(true)}
              name={t("pay_change_card")}
            />
          </Stack>
        }
      />

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        TransitionComponent={SlideUp}
        fullWidth
        maxWidth="sm"
        sx={{ "& .MuiDialog-paper": { bgcolor: theme.palette.background.dialog, borderRadius: "12px", py: 2 } }}
      >
        <DialogTitle sx={{ bgcolor: theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.background.dialog }}>
          {t("pay_update_method_dialog")}
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            sx={{ position: "absolute", right: 16, top: 16, color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Elements stripe={stripePromise}>
            <ChangeCardForm onSuccess={() => { setModalOpen(false); fetchData() }} />
          </Elements>
        </DialogContent>
      </Dialog>
    </>
  )
}


function ChangeCardForm({ onSuccess }) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation()
  const theme = useTheme()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    if (!stripe || !elements) return

    try {
      const cardElement = elements.getElement(CardElement)
      const { error: createError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      })

      if (createError) {
        setError(createError.message)
        setSubmitting(false)
        return
      }

      await axios.post(`${API_URL}/api/account/billing/change-payment-method/`, {
        payment_method_id: paymentMethod.id,
      })

      onSuccess()
      setSubmitting(false)
    } catch (err) {
      setError(err.response?.data?.message || t("pay_update_error"))
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3} mt={2}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: theme.palette.text.primary,
                "::placeholder": { color: theme.palette.text.disabled },
              },
              invalid: { color: theme.palette.error.main },
            },
          }}
        />
        {error && (
          <Typography color="error" variant="body2">{error}</Typography>
        )}
        <DialogActions>
          <ButtonForm
            name={submitting ? <CircularProgress size={24} /> : t("pay_update_card")}
            disabled={submitting}
          />
        </DialogActions>
      </Stack>
    </form>
  )
}
